import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTracker } from '../components/PageTracker';
export function LymphedemaPage() {
  // Timer State
  const [timeLeft, setTimeLeft] = useState(36 * 60);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const h = Math.floor(timeLeft / 3600).toString().padStart(2, '0');
  const m = Math.floor(timeLeft % 3600 / 60).toString().padStart(2, '0');
  const s = (timeLeft % 60).toString().padStart(2, '0');
  // Bundle Selection State
  const [selectedBundle, setSelectedBundle] = useState(1);
  // FAQ State
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // Floating CTA State
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [pricingVisible, setPricingVisible] = useState(false);
  // Checkout URLs per bundle
  const checkoutUrls: Record<number, string> = {
    1: 'https://ginextwarming.com/cart/47655498891567:1',
    2: 'https://ginextwarming.com/cart/47655498858799:1',
    3: 'https://ginextwarming.com/cart/47655498924335:1'
  };
  const bundleLabels: Record<number, string> = {
    1: '5x Ginger Oil — $89.97',
    2: '3x Ginger Oil — $59.98',
    3: '1x Ginger Oil — $29.99'
  };
  const bundleSavings: Record<number, string> = {
    1: 'Save 60%',
    2: 'Save 33%',
    3: ''
  };
  // Scroll tracking for floating CTA — use pixel threshold for reliability
  useEffect(() => {
    let isPricingInView = false;
    const handleScroll = () => {
      const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const hasScrolledEnough = scrollTop > 400;
      setShowFloatingCTA(hasScrolledEnough && !isPricingInView);
    };
    // Observe pricing section visibility
    const pricingSection = document.getElementById('pricing');
    let observer: IntersectionObserver | null = null;
    if (pricingSection) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          isPricingInView = entry.isIntersecting;
          setPricingVisible(entry.isIntersecting);
          handleScroll();
        });
      }, {
        threshold: 0.1
      });
      observer.observe(pricingSection);
    }
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    // Also listen on the document in case the scroll target differs
    document.addEventListener('scroll', handleScroll, {
      passive: true
    });
    // Initial check after a short delay to let the page render
    const timeout = setTimeout(handleScroll, 500);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
      if (observer) observer.disconnect();
    };
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  // Review Tabs State
  const [activeTab, setActiveTab] = useState('Results');
  const tabs = ['Results', 'Comfort', 'Value', 'Daily Use', 'Ingredients'];
  const faqs = [{
    q: 'What is GINEXT Ginger Oil?',
    a: 'GINEXT is a premium botanical ginger oil formulated to support circulation and relieve the pressure that causes swollen, heavy legs. It uses high-potency ginger, black pepper, eucalyptus, and rosemary to create deep, natural warmth that helps your body release tension and move fluid naturally.'
  }, {
    q: 'How is GINEXT different from typical pain or menthol creams?',
    a: "Most creams use menthol to create a cold sensation that temporarily overrides pain signals. GINEXT works differently — it uses botanical warmth to support circulation, helping your body release the pressure that's causing discomfort rather than just masking it."
  }, {
    q: 'What ingredients are in GINEXT?',
    a: 'GINEXT contains a blend of high-potency ginger root oil, black pepper extract, eucalyptus oil, rosemary oil, and a carrier base of botanical oils. No menthol, no artificial numbing agents, no harsh chemicals.'
  }, {
    q: 'What does GINEXT feel like when I use it?',
    a: "You'll feel a gentle warmth that gradually deepens — not an icy blast or aggressive tingling. The warmth spreads naturally, and most users notice their muscles beginning to relax and pressure easing within 10–15 minutes of application."
  }, {
    q: 'What kinds of leg issues is GINEXT used for?',
    a: 'GINEXT is used for swollen ankles and legs, sock marks, poor circulation, cold feet, end-of-day leg heaviness, travel swelling, and fluid buildup from prolonged sitting or standing.'
  }, {
    q: 'How do I use GINEXT?',
    a: 'Apply a few drops directly to the affected area, then massage gently into the skin. You should feel warming relief within minutes. Use daily or as needed for best results — no special ritual required.'
  }, {
    q: 'How often should I apply it?',
    a: 'Most users apply GINEXT once in the morning and once after work or in the evening. Daily use produces the best cumulative results, as circulation support builds over time.'
  }, {
    q: 'How long until I feel results?',
    a: 'Most users feel warmth and initial relief within 10–15 minutes of the first application. Noticeable reduction in swelling and pressure typically develops over 1–2 weeks of consistent daily use.'
  }, {
    q: 'Will it feel greasy or stain my clothes?',
    a: 'No. GINEXT is formulated with fast-absorbing botanical oils that leave no greasy film and do not stain clothing when allowed to absorb for 2–3 minutes before dressing.'
  }, {
    q: 'Do you offer a money-back guarantee?',
    a: "Yes. All GINEXT orders are backed by a 30-day money-back guarantee. If you're not satisfied for any reason, contact us within 30 days of delivery for a full refund — no questions asked."
  }, {
    q: 'Do you offer free shipping?',
    a: 'Yes. Free shipping is included on all bundle orders (3-bottle and 5-bottle). Single bottle orders qualify for free shipping on orders over $50.'
  }, {
    q: 'Which bundle should I choose?',
    a: "Most customers choose the 5-bottle bundle (Buy 3 Get 2 Free) because it offers the best value and ensures they don't run out once they start feeling results. Circulation support works best with consistent daily use over several weeks."
  }];
  return <div className="font-sans text-[#2D2D2D] bg-white leading-relaxed" data-id="element-2672">
      <PageTracker data-id="element-2673" />

      {/* FLOATING BUY NOW CTA */}
      <AnimatePresence data-id="element-2674">
        {showFloatingCTA && <motion.div initial={{
        opacity: 0,
        y: 100
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 100
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none" data-id="element-2675">
            <div className="max-w-[600px] mx-auto px-4 pb-4" data-id="element-2676">
              <div className="bg-white border border-[#E0D8CF] rounded-xl shadow-[0_-4px_30px_rgba(0,0,0,0.12)] pointer-events-auto overflow-hidden" data-id="element-2677">
                <div className="px-4 py-3 flex items-center justify-between gap-3" data-id="element-2678">
                  <div className="min-w-0" data-id="element-2679">
                    <p className="text-[14px] font-extrabold text-[#2D2D2D] truncate" data-id="element-2680">
                      {bundleLabels[selectedBundle]}
                    </p>
                    {bundleSavings[selectedBundle] && <p className="text-[11px] font-bold text-[#4CAF50]" data-id="element-2681">
                        {bundleSavings[selectedBundle]} • Free Shipping
                      </p>}
                  </div>
                  <a href={checkoutUrls[selectedBundle]} target="_blank" rel="noopener noreferrer" className="shrink-0 bg-[#E8720C] text-white py-3 px-6 rounded-lg text-[15px] font-extrabold transition-all hover:bg-[#C45E08] hover:-translate-y-px active:translate-y-0" data-id="element-2682">
                    Buy Now →
                  </a>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* ANNOUNCEMENT BAR */}
      <div className="bg-[#FDE8D0] text-center py-2.5 px-5 text-[13px] font-semibold text-[#2D2D2D] tracking-wide" data-id="element-2683">
        🔥 <span className="text-[#E8720C]" data-id="element-2684">Limited Time:</span> Buy 3 Get 2
        Free — Save Up to 60% Off &nbsp;|&nbsp; Free Shipping on Orders Over $50
      </div>

      {/* NAVIGATION */}
      <nav className="bg-white border-b border-[#E0D8CF] sticky top-0 z-50" data-id="element-2685">
        <div className="max-w-[1100px] mx-auto flex items-center justify-between py-3.5 px-6" data-id="element-2686">
          <div className="text-[22px] font-black tracking-tight text-[#2D2D2D]" data-id="element-2687">
            GI<span className="text-[#E8720C]" data-id="element-2688">NEXT</span>
          </div>
          <ul className="hidden md:flex gap-7 list-none text-[13px] font-medium text-[#6B6B6B]" data-id="element-2689">
            <li data-id="element-2690">
              <a href="#how-it-works" className="hover:text-[#E8720C]" data-id="element-2691">
                How It Works
              </a>
            </li>
            <li data-id="element-2692">
              <a href="#benefits" className="hover:text-[#E8720C]" data-id="element-2693">
                Benefits
              </a>
            </li>
            <li data-id="element-2694">
              <a href="#how-to-use" className="hover:text-[#E8720C]" data-id="element-2695">
                How To Use
              </a>
            </li>
            <li data-id="element-2696">
              <a href="#pricing" className="hover:text-[#E8720C]" data-id="element-2697">
                Pricing
              </a>
            </li>
            <li data-id="element-2698">
              <a href="#faq" className="hover:text-[#E8720C]" data-id="element-2699">
                FAQ
              </a>
            </li>
          </ul>
          <a href="#pricing" className="bg-[#E8720C] text-white py-2 px-5 rounded-md text-[13px] font-bold transition-colors hover:bg-[#C45E08]" data-id="element-2700">
            Get 60% Off →
          </a>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="bg-[#FAF7F2] py-16 px-6" id="hero" data-id="element-2701">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-2702">
          <div data-id="element-2703">
            <div className="inline-block bg-[#FFF0E0] text-[#E8720C] text-[11px] font-extrabold tracking-widest uppercase py-1 px-3 rounded-full border border-[#F5C89A] mb-4" data-id="element-2704">
              America's #1 Ginger Oil
            </div>
            <h1 className="text-3xl md:text-[42px] font-extrabold leading-[1.15] text-[#2D2D2D] mb-2" data-id="element-2705">
              By Evening, Your Legs
              <br data-id="element-2706" />
              Aren't Tired —<br data-id="element-2707" />
              <span className="text-[#E8720C]" data-id="element-2708">They're Holding Pressure</span>
            </h1>
            <p className="text-[15px] text-[#6B6B6B] mb-6 max-w-[440px]" data-id="element-2709">
              Tight ankles, lingering sock marks, and heavy legs are signs
              circulation has slowed. The right warmth helps your body release
              pressure — not freeze it.
            </p>
            <ul className="list-none mb-7 space-y-2.5" data-id="element-2710">
              {['Deep natural warmth — no cold shock', 'Supports blood flow & circulation', 'Releases pressure, not just sensation', '100% botanical oil — zero synthetics', 'Absorbs fast, no greasy film'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-[14px] font-medium text-[#2D2D2D]" data-id="element-2711">
                  <span className="bg-[#E8720C] text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 mt-0.5" data-id="element-2712">
                    ✓
                  </span>
                  {item}
                </li>)}
            </ul>
            <a href="#pricing" className="inline-block bg-[#E8720C] text-white py-4 px-9 rounded-lg text-[16px] font-extrabold tracking-wide text-center transition-all hover:bg-[#C45E08] hover:-translate-y-px" data-id="element-2713">
              Try GINEXT Risk-Free →
              <span className="block text-[11px] font-medium opacity-85 mt-0.5" data-id="element-2714">
                30 Days Money Back Guaranteed
              </span>
            </a>
            <div className="flex items-center gap-2.5 mt-4 text-[13px] text-[#6B6B6B]" data-id="element-2715">
              <span className="text-[#E8720C] text-[16px] tracking-widest" data-id="element-2716">
                ★★★★★
              </span>
              <div className="flex" data-id="element-2717">
                {[1, 2, 3, 4].map((_, i) => <span key={i} className="w-7 h-7 rounded-full bg-[#F2EBE0] border-2 border-white flex items-center justify-center text-[10px] text-[#6B6B6B] -ml-1.5 first:ml-0" data-id="element-2718">
                    👤
                  </span>)}
              </div>
              <span data-id="element-2719">
                Trusted by <strong data-id="element-2720">10,000+</strong> happy customers
              </span>
            </div>
          </div>
          <div className="relative" data-id="element-2721">
            <div className="w-full max-w-[480px] mx-auto rounded-2xl bg-white shadow-[0_8px_40px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-2722">
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-[#FDE8D0] to-[#F5C89A] flex flex-col items-center justify-center gap-2 rounded-2xl" data-id="element-2723">
                <span className="text-6xl" data-id="element-2724">🫙</span>
                <span className="text-lg font-extrabold text-[#E8720C] tracking-tight" data-id="element-2725">
                  GINEXT Ginger Oil
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3" data-id="element-2726">
              {[{
              icon: '🦵',
              text: 'Tight Ankles & Legs'
            }, {
              icon: '❄️',
              text: 'No Cold Shock'
            }, {
              icon: '🔄',
              text: 'Stalled Circulation'
            }, {
              icon: '🚫',
              text: 'No Menthol Overload'
            }, {
              icon: '☀️',
              text: 'Deep Natural Warmth'
            }, {
              icon: '💧',
              text: 'Releases Pressure'
            }, {
              icon: '🩸',
              text: 'Supports Blood Flow'
            }, {
              icon: '🌿',
              text: '100% Botanical Oil'
            }].map((item, i) => <div key={i} className="bg-[#E8720C] text-white rounded-lg py-2.5 px-1.5 text-center text-[10px] font-bold leading-tight" data-id="element-2727">
                  <span className="block text-lg mb-1" data-id="element-2728">{item.icon}</span>
                  {item.text}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="bg-white py-16 px-6 text-center" id="problem" data-id="element-2729">
        <div className="w-12 h-12 rounded-full border-2 border-[#E0D8CF] flex items-center justify-center text-xl mx-auto mb-5" data-id="element-2730">
          ⚠
        </div>
        <h2 className="text-[26px] md:text-[34px] font-extrabold text-[#2D2D2D] leading-[1.2] max-w-[640px] mx-auto mb-3" data-id="element-2731">
          Why Most Creams Fail the Moment You Need Them Most
        </h2>
        <p className="text-[15px] text-[#6B6B6B] max-w-[560px] mx-auto mb-4" data-id="element-2732">
          Cold creams don't fix tension. They shock the skin. Overload the
          nerves. Silence sensation without relaxing what's holding tight.
        </p>
        <p className="text-[15px] text-[#6B6B6B] mb-4" data-id="element-2733">
          So things feel better briefly… then stiffen again by evening.
        </p>
        <div className="inline-block bg-[#F5F0EB] border border-[#E0D8CF] rounded-full py-2 px-5 text-[13px] italic text-[#2D2D2D] mt-4" data-id="element-2734">
          Tension isn't damage. It's sustained contraction.
        </div>
      </section>

      {/* DIFFERENTIATOR SECTION */}
      <section className="bg-[#F5F0EB] py-[72px] px-6" id="benefits" data-id="element-2735">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-2 gap-15 items-center" data-id="element-2736">
          <div data-id="element-2737">
            <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] mb-3" data-id="element-2738">
              The GINEXT Difference
            </div>
            <div className="text-[38px] font-black leading-[1.1] text-[#2D2D2D] mb-1" data-id="element-2739">
              Stop Numbing.
            </div>
            <div className="text-[38px] font-black leading-[1.1] text-[#E8720C] mb-5" data-id="element-2740">
              Start Releasing.
            </div>
            <div className="space-y-3 text-[15px] text-[#6B6B6B]" data-id="element-2741">
              <p data-id="element-2742">
                GINEXT isn't another menthol gel competing to feel stronger.
              </p>
              <p data-id="element-2743">
                It's a circulation-supporting botanical oil designed to create
                deep, steady warmth — the signal your body recognizes as
                permission to loosen, relax, and move fluid again.
              </p>
              <p className="text-[#2D2D2D]" data-id="element-2744">
                <strong data-id="element-2745">No cold shock. No burning heat. No greasy film.</strong>
              </p>
              <p className="text-[#E8720C] font-semibold italic" data-id="element-2746">
                Just fast, noticeable support that feels natural and actually
                holds.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-2747">
            <div className="rounded-xl overflow-hidden bg-[#F2EBE0]" data-id="element-2748">
              <div className="aspect-[4/3] flex flex-col items-center justify-center gap-1.5 text-3xl" data-id="element-2749">
                🌿
              </div>
              <div className="text-[11px] text-[#6B6B6B] italic text-center p-2" data-id="element-2750">
                Experience natural relief that supports your body's healing
                process
              </div>
            </div>
            <div className="rounded-xl overflow-hidden bg-[#F2EBE0]" data-id="element-2751">
              <div className="aspect-[4/3] flex flex-col items-center justify-center gap-1.5 text-3xl" data-id="element-2752">
                🫙
              </div>
              <div className="text-[11px] text-[#6B6B6B] italic text-center p-2" data-id="element-2753">
                High-potency ginger and botanical oils, formulated for deep,
                natural warmth
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-[72px] px-6" id="how-it-works" data-id="element-2754">
        <div className="max-w-[1100px] mx-auto" data-id="element-2755">
          <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] text-center mb-3" data-id="element-2756">
            How It Works
          </div>
          <h2 className="text-[26px] md:text-[34px] font-extrabold text-center text-[#2D2D2D] leading-[1.2] mb-3" data-id="element-2757">
            What Actually Matters
          </h2>
          <p className="text-[15px] text-[#6B6B6B] text-center max-w-[560px] mx-auto mb-10" data-id="element-2758">
            Most products stay on the surface.{' '}
            <span className="text-[#E8720C] font-semibold" data-id="element-2759">
              GINEXT works beneath it.
            </span>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10" data-id="element-2760">
            {[{
            icon: '🫚',
            name: 'Ginger + Black Pepper',
            desc: 'Create deep, natural warmth that stimulates blood flow and supports circulation'
          }, {
            icon: '🌿',
            name: 'Eucalyptus',
            desc: 'Helps calm pain signals without dulling sensation or numbing the area'
          }, {
            icon: '🌱',
            name: 'Rosemary',
            desc: 'Supports circulation and tissue recovery for lasting comfort'
          }, {
            icon: '💧',
            name: 'Botanical Oils',
            desc: 'Absorbs quickly, clean finish — no residue, no greasy feeling'
          }].map((item, i) => <div key={i} className="bg-[#FAF7F2] rounded-xl p-6 text-center" data-id="element-2761">
                <div className="text-[28px] mb-3" data-id="element-2762">{item.icon}</div>
                <div className="text-[14px] font-extrabold text-[#2D2D2D] mb-1.5" data-id="element-2763">
                  {item.name}
                </div>
                <div className="text-[12px] text-[#6B6B6B] leading-[1.5]" data-id="element-2764">
                  {item.desc}
                </div>
              </div>)}
          </div>
          <p className="text-center mt-8 text-[15px] italic text-[#6B6B6B]" data-id="element-2765">
            <em data-id="element-2766">
              The result? Warmth spreads. Tension eases. Movement returns.
            </em>
          </p>
        </div>
      </section>

      {/* WHAT YOU'LL FEEL */}
      <section className="bg-[#2A2A2A] text-white py-[72px] px-6" data-id="element-2767">
        <div className="max-w-[1100px] mx-auto" data-id="element-2768">
          <div className="grid md:grid-cols-2 gap-15 items-start mb-12" data-id="element-2769">
            <div data-id="element-2770">
              <h2 className="text-[30px] font-black leading-[1.2] uppercase mb-4" data-id="element-2771">
                What You'll Feel
                <br data-id="element-2772" />
                (And What You Won't)
              </h2>
              <p className="text-[14px] text-[#aaa] leading-[1.6]" data-id="element-2773">
                You won't feel an icy blast. You won't feel aggressive tingling.
                You'll feel warmth arrive gradually, then deepen.
              </p>
            </div>
            <ul className="list-none space-y-3" data-id="element-2774">
              {['Tightness easing instead of being overridden', 'Muscles relaxing instead of bracing', 'Comfort that builds instead of fading', 'Skin that feels normal, not coated', 'Relief that fits daily life, not emergencies only'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-[14px] text-[#ddd]" data-id="element-2775">
                  <span className="text-[#E8720C] font-black shrink-0" data-id="element-2776">✓</span>
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-2777">
            <div className="rounded-xl overflow-hidden relative" data-id="element-2778">
              <div className="absolute top-3 left-3 text-[11px] font-bold py-1 px-2.5 rounded bg-white/20 text-[#ccc]" data-id="element-2779">
                Before GINEXT
              </div>
              <div className="aspect-[4/3] flex items-center justify-center text-[40px] bg-[#3a3a3a]" data-id="element-2780">
                🦵
              </div>
            </div>
            <div className="rounded-xl overflow-hidden relative" data-id="element-2781">
              <div className="absolute top-3 left-3 text-[11px] font-bold py-1 px-2.5 rounded bg-[#E8720C] text-white" data-id="element-2782">
                After GINEXT Use
              </div>
              <div className="aspect-[4/3] flex items-center justify-center text-[40px] bg-[#4a3020]" data-id="element-2783">
                ✨
              </div>
              <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] font-bold py-1.5 px-2.5 rounded-md text-center leading-[1.4]" data-id="element-2784">
                100% Natural
                <br data-id="element-2785" />
                Zero Synthetics
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="bg-[#FAF7F2] py-[72px] px-6" data-id="element-2786">
        <div className="max-w-[1100px] mx-auto" data-id="element-2787">
          <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] text-center mb-3" data-id="element-2788">
            Real Results
          </div>
          <h2 className="text-[26px] md:text-[34px] font-extrabold text-center text-[#2D2D2D] leading-[1.2] mb-3" data-id="element-2789">
            What Our Customers Report
          </h2>
          <p className="text-[15px] text-[#6B6B6B] text-center max-w-[560px] mx-auto mb-10" data-id="element-2790">
            After consistent daily use, here's what thousands of GINEXT
            customers experienced.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10" data-id="element-2791">
            {[{
            num: '92%',
            desc: 'felt noticeable warmth and relief within the first 10 minutes of application'
          }, {
            num: '87%',
            desc: 'reported lighter, less swollen legs by end of day after 2 weeks of use'
          }, {
            num: '89%',
            desc: 'said GINEXT felt more natural and comfortable than their previous product'
          }, {
            num: '94%',
            desc: 'would recommend GINEXT to a friend dealing with leg pressure or swelling'
          }].map((stat, i) => <div key={i} className="text-center" data-id="element-2792">
                <div className="text-[56px] font-black text-[#E8720C] leading-none mb-2" data-id="element-2793">
                  {stat.num}
                </div>
                <div className="text-[13px] text-[#6B6B6B] leading-[1.5]" data-id="element-2794">
                  {stat.desc}
                </div>
              </div>)}
          </div>
          <p className="text-center text-[12px] text-[#aaa] italic mt-6" data-id="element-2795">
            *Based on a post-purchase survey of 3,000+ customers who have been
            using GINEXT daily.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#F5F0EB] py-[72px] px-6" id="testimonials" data-id="element-2796">
        <div className="max-w-[1100px] mx-auto" data-id="element-2797">
          <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] text-center mb-3" data-id="element-2798">
            ★★★★★ 4.8 Stars
          </div>
          <h2 className="text-[26px] md:text-[34px] font-extrabold text-center text-[#2D2D2D] leading-[1.2] mb-9" data-id="element-2799">
            Join 10,000+ Others Finding Relief
          </h2>
          <div className="flex gap-2 justify-center flex-wrap mb-9" data-id="element-2800">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`py-2 px-4.5 rounded-full border-[1.5px] text-[13px] font-semibold transition-all ${activeTab === tab ? 'bg-[#E8720C] border-[#E8720C] text-white' : 'bg-white border-[#E0D8CF] text-[#6B6B6B] hover:border-[#E8720C]'}`} data-id="element-2801">
                {tab}
              </button>)}
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-id="element-2802">
            {[{
            title: `"By night, my feet don't feel swollen anymore."`,
            body: "I'm on my feet all day. With GINEXT, that pressure just doesn't pile up the same way. I apply it in the morning and again after work — total game changer.",
            name: 'Mark R., 48'
          }, {
            title: `"No shock. No residue. Just warmth my body responds to."`,
            body: "It absorbs fast and actually feels supportive. I've tried every menthol cream out there and nothing ever felt this natural. I don't dread putting it on.",
            name: 'Elena M., 38'
          }, {
            title: `"I thought foot pain meant overuse. It was pressure."`,
            body: 'My doctor suggested better circulation support. GINEXT was the first thing that made sense. It was pressure, not injury. This finally made sense to my body.',
            name: 'Denise L., 54'
          }].map((review, i) => <div key={i} className="bg-white rounded-xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.05)]" data-id="element-2803">
                <div className="text-[#E8720C] text-[14px] mb-2" data-id="element-2804">★★★★★</div>
                <div className="text-[14px] font-extrabold text-[#2D2D2D] mb-2" data-id="element-2805">
                  {review.title}
                </div>
                <div className="text-[13px] text-[#6B6B6B] leading-[1.6] mb-3" data-id="element-2806">
                  {review.body}
                </div>
                <div className="text-[12px] font-bold text-[#E8720C]" data-id="element-2807">
                  {review.name}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-white py-[72px] px-6" data-id="element-2808">
        <div className="max-w-[1100px] mx-auto" data-id="element-2809">
          <h2 className="text-[26px] md:text-[34px] font-extrabold text-center text-[#2D2D2D] leading-[1.2] mb-3" data-id="element-2810">
            Built for Real Legs. Real Routines.
          </h2>
          <p className="text-[15px] text-[#6B6B6B] text-center max-w-[560px] mx-auto mb-8" data-id="element-2811">
            GINEXT is used daily by people dealing with:
          </p>
          <div className="grid md:grid-cols-2 gap-3 max-w-[700px] mx-auto" data-id="element-2812">
            {[{
            icon: '🦵',
            text: 'Swollen ankles & legs'
          }, {
            icon: '🧦',
            text: 'Sock marks & tight ankles'
          }, {
            icon: '🩸',
            text: 'Poor circulation & cold feet'
          }, {
            icon: '🪑',
            text: 'Desk job or long-standing pressure'
          }, {
            icon: '🌙',
            text: 'End-of-day leg heaviness'
          }, {
            icon: '✈️',
            text: 'Travel swelling & fluid buildup'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-[#FAF7F2] border border-[#E0D8CF] rounded-lg py-3.5 px-4.5 text-[14px] font-medium text-[#2D2D2D]" data-id="element-2813">
                <span className="text-[18px] text-[#E8720C]" data-id="element-2814">{item.icon}</span>
                {item.text}
              </div>)}
          </div>
          <p className="text-center text-[13px] italic text-[#6B6B6B] mt-6" data-id="element-2815">
            <em data-id="element-2816">
              This isn't emergency relief. This is daily support that prevents
              buildup.
            </em>
          </p>
        </div>
      </section>

      {/* HOW TO USE */}
      <section className="bg-[#FAF7F2] py-[72px] px-6" id="how-to-use" data-id="element-2817">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-[1fr_2fr] gap-15 items-start" data-id="element-2818">
          <div data-id="element-2819">
            <h2 className="text-[30px] font-black mb-2" data-id="element-2820">How To Use</h2>
            <p className="text-[14px] text-[#6B6B6B]" data-id="element-2821">
              No rituals. No waiting. Just relief that keeps pace with you.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-2822">
            {['Apply a few drops to the affected area', 'Massage gently into the skin', 'Feel warming relief within minutes', 'Use daily or as needed for best results'].map((step, i) => <div key={i} className="flex items-start gap-3 text-[14px] text-[#2D2D2D]" data-id="element-2823">
                <div className="w-7 h-[3px] bg-[#E8720C] shrink-0 mt-2.5" data-id="element-2824"></div>
                <span data-id="element-2825">{step}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CLEAN FORMULA */}
      <section className="bg-white py-[72px] px-6" data-id="element-2826">
        <div className="max-w-[1100px] mx-auto" data-id="element-2827">
          <h2 className="text-[26px] md:text-[34px] font-extrabold text-center text-[#2D2D2D] leading-[1.2] mb-3" data-id="element-2828">
            Clean, Controlled, Trusted
          </h2>
          <p className="text-[15px] text-[#6B6B6B] text-center max-w-[560px] mx-auto mb-8" data-id="element-2829">
            Just functional botanicals, formulated for speed and effectiveness.
          </p>
          <div className="grid md:grid-cols-2 gap-3 max-w-[700px] mx-auto" data-id="element-2830">
            {['No menthol overload', 'No artificial numbing agents', 'No harsh chemicals', 'No gimmicks or fillers'].map((item, i) => <div key={i} className="flex items-center justify-between bg-[#FAF7F2] border border-[#E0D8CF] rounded-lg py-3.5 px-4.5 text-[14px] font-medium" data-id="element-2831">
                <span data-id="element-2832">
                  <span className="text-[#4CAF50] font-bold mr-1" data-id="element-2833">✓</span>{' '}
                  {item}
                </span>
                <span className="text-[#E53935] font-bold text-[18px]" data-id="element-2834">✕</span>
              </div>)}
          </div>
          <div className="flex items-center justify-center gap-2 mt-6 text-[13px] font-bold text-[#6B6B6B]" data-id="element-2835">
            <span className="text-[16px]" data-id="element-2836">🛡</span> 100% Natural Botanical
            Formula
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-[#F5F0EB] py-[72px] px-6" data-id="element-2837">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-[1fr_2fr] gap-15 items-start" data-id="element-2838">
          <div data-id="element-2839">
            <h2 className="text-[28px] font-black mb-3" data-id="element-2840">
              <span className="text-[#E8720C]" data-id="element-2841">Us</span> vs. Them
            </h2>
            <p className="text-[14px] text-[#6B6B6B]" data-id="element-2842">
              Not overhyped or overpriced. Just real circulation support that
              works with your body, not against it.
            </p>
          </div>
          <div className="overflow-x-auto" data-id="element-2843">
            <table className="w-full border-collapse min-w-[500px]" data-id="element-2844">
              <thead data-id="element-2845">
                <tr data-id="element-2846">
                  <th className="p-3 text-left border-b border-[#E0D8CF]" data-id="element-2847"></th>
                  <th className="p-3 text-center border-b border-[#E0D8CF] bg-[#E8720C] text-white rounded-t-lg font-extrabold text-[14px]" data-id="element-2848">
                    GINEXT
                  </th>
                  <th className="p-3 text-center border-b border-[#E0D8CF] font-extrabold text-[14px]" data-id="element-2849">
                    Menthol Cream
                  </th>
                  <th className="p-3 text-center border-b border-[#E0D8CF] font-extrabold text-[14px]" data-id="element-2850">
                    Generic Oil
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2851">
                {[{
                label: 'Cost Per Use',
                g: '$1.47',
                m: '$2.10',
                o: '$0.90'
              }, {
                label: 'Supports Circulation',
                g: '✓',
                m: '✕',
                o: '✕'
              }, {
                label: 'No Cold Shock',
                g: '✓',
                m: '✕',
                o: '✓'
              }, {
                label: 'Absorbs Cleanly',
                g: '✓',
                m: '✕',
                o: '✕'
              }, {
                label: 'Botanical Formula',
                g: '✓',
                m: '✕',
                o: '✓'
              }, {
                label: 'Daily Use Friendly',
                g: '✓',
                m: '✕',
                o: '✕'
              }].map((row, i) => <tr key={i} data-id="element-2852">
                    <td className="p-3 text-left border-b border-[#E0D8CF] text-[13px] font-medium text-[#2D2D2D]" data-id="element-2853">
                      {row.label}
                    </td>
                    <td className="p-3 text-center border-b border-[#E0D8CF] text-[13px] bg-[#FFF0E0] text-[#E8720C] font-bold" data-id="element-2854">
                      {row.g === '✓' ? <span className="text-[#4CAF50] text-[16px]" data-id="element-2855">✓</span> : row.g}
                    </td>
                    <td className="p-3 text-center border-b border-[#E0D8CF] text-[13px]" data-id="element-2856">
                      {row.m === '✕' ? <span className="text-[#ccc] text-[16px]" data-id="element-2857">✕</span> : row.m}
                    </td>
                    <td className="p-3 text-center border-b border-[#E0D8CF] text-[13px]" data-id="element-2858">
                      {row.o === '✕' ? <span className="text-[#ccc] text-[16px]" data-id="element-2859">✕</span> : row.o === '✓' ? <span className="text-[#4CAF50] text-[16px]" data-id="element-2860">✓</span> : row.o}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PRICING / BUNDLES */}
      <section className="bg-white py-[72px] px-6" id="pricing" data-id="element-2861">
        <div className="max-w-[1100px] mx-auto" data-id="element-2862">
          <div className="text-center mb-3" data-id="element-2863">
            <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] mb-2" data-id="element-2864">
              Choose Your Bundle
            </div>
            <h2 className="text-[26px] md:text-[34px] font-extrabold text-[#2D2D2D] leading-[1.2] mb-3" data-id="element-2865">
              Relief Works Better When It's Consistent
            </h2>
            <p className="text-[15px] text-[#6B6B6B] max-w-[560px] mx-auto" data-id="element-2866">
              Most people use GINEXT daily. Choose a bundle that supports real
              recovery — not just a one-time application.
            </p>
          </div>

          <div className="bg-[#FAF7F2] border border-[#E0D8CF] rounded-xl p-4 md:p-6 flex flex-col md:flex-row justify-between items-center mb-4 gap-4" data-id="element-2867">
            <div data-id="element-2868">
              <div className="text-[11px] font-bold text-[#E8720C] uppercase tracking-widest mb-1" data-id="element-2869">
                Limited Time Offer
              </div>
              <div className="text-[22px] font-black text-[#2D2D2D]" data-id="element-2870">
                Save Up to <span className="text-[#E8720C]" data-id="element-2871">60% Off</span>
              </div>
              <div className="text-[13px] text-[#6B6B6B]" data-id="element-2872">
                Special pricing on 3 & 5 bottle bundles
              </div>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-[#6B6B6B]" data-id="element-2873">
              <span data-id="element-2874">⏱ Offer Ends In</span>
              <span className="bg-[#2D2D2D] text-white py-1.5 px-2.5 rounded text-[16px] font-extrabold font-mono" data-id="element-2875">
                {h}
              </span>
              <span className="bg-[#2D2D2D] text-white py-1.5 px-2.5 rounded text-[16px] font-extrabold font-mono" data-id="element-2876">
                {m}
              </span>
              <span className="bg-[#2D2D2D] text-white py-1.5 px-2.5 rounded text-[16px] font-extrabold font-mono" data-id="element-2877">
                {s}
              </span>
            </div>
          </div>

          <div className="bg-[#FFF8E1] border border-[#FFE082] rounded-lg py-2.5 px-4 text-[13px] text-[#8D6E00] flex items-center gap-2 mb-5" data-id="element-2878">
            ⚠{' '}
            <span data-id="element-2879">
              <strong data-id="element-2880">Low Stock Alert:</strong> Due to high demand, inventory is
              running low. Secure your bundle now to avoid missing out.
            </span>
          </div>

          {/* Bundle 1 */}
          <div className={`border-2 rounded-xl overflow-hidden mb-4 transition-colors cursor-pointer ${selectedBundle === 1 ? 'border-[#E8720C]' : 'border-[#E0D8CF]'}`} onClick={() => setSelectedBundle(1)} data-id="element-2881">
            <div className={`flex justify-between items-center py-2.5 px-5 text-[13px] font-bold ${selectedBundle === 1 ? 'bg-[#E8720C] text-white' : 'bg-[#2D2D2D] text-white'}`} data-id="element-2882">
              <span className="flex items-center gap-2" data-id="element-2883">
                Buy 3 Get 2 Free{' '}
                <span className="bg-[#4CAF50] text-white py-0.5 px-2.5 rounded-full text-[11px] font-extrabold" data-id="element-2884">
                  Save 60%
                </span>
              </span>
              <span className={`text-[11px] font-bold ${selectedBundle === 1 ? 'text-white/80' : 'text-[#aaa]'}`} data-id="element-2885">
                🚚 FREE SHIPPING
              </span>
            </div>
            <div className="p-5 flex gap-4 items-center" data-id="element-2886">
              <div className="w-20 h-20 rounded-lg bg-[#F2EBE0] flex items-center justify-center text-[32px] shrink-0" data-id="element-2887">
                🫙
              </div>
              <div className="flex-1" data-id="element-2888">
                <div className="text-[15px] font-extrabold text-[#E8720C] mb-0.5" data-id="element-2889">
                  5x Ginger Oil
                </div>
                <div className="text-[12px] text-[#6B6B6B] mb-1.5" data-id="element-2890">
                  5x GINEXT Ginger Oil — Same as $29.99/each
                </div>
                <div className="text-[22px] font-black text-[#2D2D2D]" data-id="element-2891">
                  $17.99
                  <span className="text-[12px] text-[#6B6B6B] font-normal" data-id="element-2892">
                    /each
                  </span>{' '}
                  <span className="text-[14px] text-[#aaa] line-through font-normal ml-1.5" data-id="element-2893">
                    $29.99
                  </span>
                </div>
                <div className="text-[13px] font-bold text-[#2D2D2D]" data-id="element-2894">
                  Total: $89.97
                </div>
              </div>
              <div className={`w-5.5 h-5.5 border-2 rounded flex items-center justify-center shrink-0 ${selectedBundle === 1 ? 'bg-[#E8720C] border-[#E8720C] text-white text-[12px] font-black' : 'border-[#E0D8CF]'}`} data-id="element-2895">
                {selectedBundle === 1 && '✓'}
              </div>
            </div>
            {selectedBundle === 1 && <div className="bg-[#3a2010] p-3 px-5 grid grid-cols-2 gap-y-1 gap-x-6" data-id="element-2896">
                {['5x GINEXT Ginger Oil', '100% Natural Ginger', 'Premium Grade', 'Warming Relief', 'Natural Formula', 'Anti-Inflammatory', 'Fast Acting', 'Circulation Support'].map((f, i) => <div key={i} className="text-[12px] text-[#ccc]" data-id="element-2897">
                    {f}
                  </div>)}
              </div>}
            <div className="py-2.5 px-5 flex justify-between items-center text-[12px] text-[#6B6B6B]" data-id="element-2898">
              <span data-id="element-2899">
                <span className="text-[#E53935] font-bold" data-id="element-2900">
                  Sell-Out Risk: High
                </span>{' '}
                &nbsp;•&nbsp; FREE SHIPPING &nbsp;•&nbsp; Discount: 60%
              </span>
              <span className="bg-[#2D2D2D] text-white py-1 px-2.5 rounded text-[11px] font-bold" data-id="element-2901">
                ✓ Best Seller
              </span>
            </div>
          </div>

          {/* Bundle 2 */}
          <div className={`border-2 rounded-xl overflow-hidden mb-4 transition-colors cursor-pointer ${selectedBundle === 2 ? 'border-[#E8720C]' : 'border-[#E0D8CF]'}`} onClick={() => setSelectedBundle(2)} data-id="element-2902">
            <div className={`flex justify-between items-center py-2.5 px-5 text-[13px] font-bold ${selectedBundle === 2 ? 'bg-[#E8720C] text-white' : 'bg-[#2D2D2D] text-white'}`} data-id="element-2903">
              <span className="flex items-center gap-2" data-id="element-2904">
                Buy 2 Get 1 Free{' '}
                <span className="bg-[#4CAF50] text-white py-0.5 px-2.5 rounded-full text-[11px] font-extrabold" data-id="element-2905">
                  Save 33%
                </span>
              </span>
              <span className={`text-[11px] font-bold ${selectedBundle === 2 ? 'text-white/80' : 'text-[#aaa]'}`} data-id="element-2906">
                🚚 FREE SHIPPING
              </span>
            </div>
            <div className="p-5 flex gap-4 items-center" data-id="element-2907">
              <div className="w-20 h-20 rounded-lg bg-[#F2EBE0] flex items-center justify-center text-[32px] shrink-0" data-id="element-2908">
                🫙
              </div>
              <div className="flex-1" data-id="element-2909">
                <div className="text-[15px] font-extrabold text-[#E8720C] mb-0.5" data-id="element-2910">
                  3x Ginger Oil
                </div>
                <div className="text-[12px] text-[#6B6B6B] mb-1.5" data-id="element-2911">
                  3x GINEXT Ginger Oil — Same as $29.99/each
                </div>
                <div className="text-[22px] font-black text-[#2D2D2D]" data-id="element-2912">
                  $19.99
                  <span className="text-[12px] text-[#6B6B6B] font-normal" data-id="element-2913">
                    /each
                  </span>{' '}
                  <span className="text-[14px] text-[#aaa] line-through font-normal ml-1.5" data-id="element-2914">
                    $29.99
                  </span>
                </div>
                <div className="text-[13px] font-bold text-[#2D2D2D]" data-id="element-2915">
                  Total: $59.98
                </div>
              </div>
              <div className={`w-5.5 h-5.5 border-2 rounded flex items-center justify-center shrink-0 ${selectedBundle === 2 ? 'bg-[#E8720C] border-[#E8720C] text-white text-[12px] font-black' : 'border-[#E0D8CF]'}`} data-id="element-2916">
                {selectedBundle === 2 && '✓'}
              </div>
            </div>
            {selectedBundle === 2 && <div className="bg-[#3a2010] p-3 px-5 grid grid-cols-2 gap-y-1 gap-x-6" data-id="element-2917">
                {['3x GINEXT Ginger Oil', '100% Natural Ginger', 'Premium Grade', 'Warming Relief', 'Natural Formula', 'Anti-Inflammatory', 'Fast Acting', 'Circulation Support'].map((f, i) => <div key={i} className="text-[12px] text-[#ccc]" data-id="element-2918">
                    {f}
                  </div>)}
              </div>}
            <div className="py-2.5 px-5 flex justify-between items-center text-[12px] text-[#6B6B6B]" data-id="element-2919">
              <span data-id="element-2920">
                <span className="text-[#E53935] font-bold" data-id="element-2921">
                  Sell-Out Risk: High
                </span>{' '}
                &nbsp;•&nbsp; FREE SHIPPING &nbsp;•&nbsp; Discount: 33%
              </span>
              <span className="bg-[#2D2D2D] text-white py-1 px-2.5 rounded text-[11px] font-bold" data-id="element-2922">
                ✓ Popular
              </span>
            </div>
          </div>

          {/* Bundle 3 */}
          <div className={`border-2 rounded-xl overflow-hidden mb-4 transition-colors cursor-pointer ${selectedBundle === 3 ? 'border-[#E8720C]' : 'border-[#E0D8CF]'}`} onClick={() => setSelectedBundle(3)} data-id="element-2923">
            <div className={`flex justify-between items-center py-2.5 px-5 text-[13px] font-bold ${selectedBundle === 3 ? 'bg-[#E8720C] text-white' : 'bg-[#2D2D2D] text-white'}`} data-id="element-2924">
              <span data-id="element-2925">Single Bottle</span>
            </div>
            <div className="p-5 flex gap-4 items-center" data-id="element-2926">
              <div className="w-20 h-20 rounded-lg bg-[#F2EBE0] flex items-center justify-center text-[32px] shrink-0" data-id="element-2927">
                🫙
              </div>
              <div className="flex-1" data-id="element-2928">
                <div className="text-[15px] font-extrabold text-[#E8720C] mb-0.5" data-id="element-2929">
                  1x Ginger Oil
                </div>
                <div className="text-[12px] text-[#6B6B6B] mb-1.5" data-id="element-2930">
                  1x GINEXT Ginger Oil
                </div>
                <div className="text-[22px] font-black text-[#2D2D2D]" data-id="element-2931">
                  $29.99
                  <span className="text-[12px] text-[#6B6B6B] font-normal" data-id="element-2932">
                    /each
                  </span>
                </div>
                <div className="text-[13px] font-bold text-[#2D2D2D]" data-id="element-2933">
                  Total: $29.99
                </div>
              </div>
              <div className={`w-5.5 h-5.5 border-2 rounded flex items-center justify-center shrink-0 ${selectedBundle === 3 ? 'bg-[#E8720C] border-[#E8720C] text-white text-[12px] font-black' : 'border-[#E0D8CF]'}`} data-id="element-2934">
                {selectedBundle === 3 && '✓'}
              </div>
            </div>
            <div className="py-2.5 px-5 flex justify-between items-center text-[12px] text-[#6B6B6B]" data-id="element-2935">
              <span data-id="element-2936">
                <span className="text-[#E53935] font-bold" data-id="element-2937">
                  Sell-Out Risk: High
                </span>{' '}
                &nbsp;•&nbsp; Discount: 0%
              </span>
            </div>
          </div>

          <div className="text-center text-[13px] text-[#6B6B6B] italic my-4 leading-[1.8]" data-id="element-2938">
            Most customers choose a bundle to avoid reordering once relief
            begins.
            <br data-id="element-2939" />
            The 30-day money-back guarantee applies to all bundles — no risk in
            choosing the option that lasts longer.
            <br data-id="element-2940" />
            Choose the option that supports how you actually want to feel — not
            just today, but going forward.
          </div>

          <button className="block w-full bg-[#E8720C] text-white p-4.5 rounded-xl text-[18px] font-black text-center transition-colors hover:bg-[#C45E08]" data-id="element-2941">
            Get {selectedBundle === 1 ? '5' : selectedBundle === 2 ? '3' : '1'}{' '}
            Bottle{selectedBundle !== 3 && 's'} for $
            {selectedBundle === 1 ? '89.97' : selectedBundle === 2 ? '59.98' : '29.99'}{' '}
            →
            <span className="block text-[12px] font-medium mt-0.5" data-id="element-2942">
              30 Days Money Back Guaranteed
            </span>
          </button>
        </div>
      </section>

      {/* CTA STRIP */}
      <div className="bg-[#FAF7F2] border-y border-[#E0D8CF] py-3.5 px-6 text-center text-[13px] font-semibold text-[#6B6B6B]" data-id="element-2943">
        🚚 Free Shipping &nbsp;•&nbsp; 🔒 Secure Checkout &nbsp;•&nbsp; ↩
        30-Day Money Back Guarantee
      </div>

      {/* FAQ SECTION */}
      <section className="bg-white py-[72px] px-6" id="faq" data-id="element-2944">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-[1fr_2fr] gap-15 items-start" data-id="element-2945">
          <div data-id="element-2946">
            <h2 className="text-[28px] font-black leading-[1.2]" data-id="element-2947">
              Any last questions?
            </h2>
          </div>
          <div className="max-w-[760px] mx-auto w-full" data-id="element-2948">
            {faqs.map((faq, i) => <div key={i} className="border-b border-[#E0D8CF]" data-id="element-2949">
                <button className="w-full text-left py-4.5 text-[15px] font-medium text-[#2D2D2D] flex justify-between items-center gap-3 hover:text-[#E8720C]" onClick={() => toggleFaq(i)} data-id="element-2950">
                  {faq.q}
                  <span className="text-[20px] text-[#6B6B6B] shrink-0" data-id="element-2951">
                    {openFaq === i ? '−' : '+'}
                  </span>
                </button>
                {openFaq === i && <div className="text-[14px] text-[#6B6B6B] leading-[1.7] pb-4.5" data-id="element-2952">
                    {faq.a}
                  </div>}
              </div>)}
            <div className="text-center mt-8" data-id="element-2953">
              <a href="#pricing" className="text-[#E8720C] text-[14px] font-bold" data-id="element-2954">
                Explore Bundles →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="bg-[#2A2A2A] text-white py-[72px] px-6 text-center" data-id="element-2955">
        <div className="text-[11px] font-extrabold tracking-widest uppercase text-[#E8720C] mb-4" data-id="element-2956">
          Why GINEXT Feels Different
        </div>
        <p className="text-[15px] text-[#aaa] max-w-[560px] mx-auto mb-4" data-id="element-2957">
          Because it is. Most products try to silence sensation.
          <br data-id="element-2958" />
          GINEXT supports circulation so pressure can release naturally.
        </p>
        <p className="text-[15px] text-[#aaa] max-w-[560px] mx-auto mb-4" data-id="element-2959">
          That's the difference between masking heaviness and helping your legs
          feel lighter again.
        </p>
        <div className="text-[30px] md:text-[44px] font-black leading-[1.1] mb-2" data-id="element-2960">
          Feel the Warmth.
          <br data-id="element-2961" />
          <span className="text-[#E8720C]" data-id="element-2962">Release the Pressure.</span>
        </div>
        <div className="text-[15px] text-[#aaa] mb-8" data-id="element-2963">
          Move through your day lighter.
        </div>
        <a href="#pricing" className="inline-block bg-[#E8720C] text-white py-4 px-10 rounded-lg text-[16px] font-extrabold transition-colors hover:bg-[#C45E08]" data-id="element-2964">
          Try GINEXT Risk-Free →
          <span className="block text-[11px] font-medium mt-0.5" data-id="element-2965">
            30 Days Money Back Guaranteed
          </span>
        </a>
        <div className="text-[12px] text-[#888] mt-3" data-id="element-2966">
          Free Shipping on Orders Over $50
        </div>
        <div data-id="element-2967">
          <div className="inline-flex items-center gap-2.5 bg-white text-[#2D2D2D] rounded-lg py-2.5 px-4 text-[12px] mt-8 shadow-[0_4px_20px_rgba(0,0,0,0.3)] text-left" data-id="element-2968">
            <span className="text-[24px]" data-id="element-2969">🫙</span>
            <div data-id="element-2970">
              <strong data-id="element-2971">Matthew</strong> from <strong data-id="element-2972">Kansas City</strong>
              <br data-id="element-2973" />
              <span className="text-[11px] text-[#6B6B6B]" data-id="element-2974">
                Just purchased GINEXT — Buy 3 Get 2 Free
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2A2A2A] text-white pt-[60px] pb-8 px-6" data-id="element-2975">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-[1.5fr_1fr_1fr] gap-12 mb-10" data-id="element-2976">
          <div data-id="element-2977">
            <div className="text-[28px] font-black tracking-tight mb-4" data-id="element-2978">
              GI<span className="text-[#E8720C]" data-id="element-2979">NEXT</span>
            </div>
            <p className="text-[13px] text-[#888] mb-4" data-id="element-2980">Sign Up for 60% Off</p>
            <div className="flex gap-2 mb-3" data-id="element-2981">
              <input type="email" className="flex-1 py-2.5 px-3.5 rounded-md border border-[#555] bg-[#3a3a3a] text-white text-[13px] placeholder:text-[#888]" placeholder="Phone or email" data-id="element-2982" />
              <button className="bg-[#E8720C] text-white border-none py-2.5 px-4 rounded-md text-[16px] cursor-pointer" data-id="element-2983">
                →
              </button>
            </div>
            <p className="text-[11px] text-[#666] leading-[1.6]" data-id="element-2984">
              By providing your contact information, you agree to receive
              marketing communications. Reply STOP to opt-out. View Privacy
              Policy.
            </p>
          </div>
          <div data-id="element-2985">
            <div className="text-[14px] font-extrabold text-[#E8720C] mb-4" data-id="element-2986">
              Learn
            </div>
            <ul className="list-none space-y-2" data-id="element-2987">
              {['Reviews', 'Our Science', 'How GINEXT Works', 'Our Story', 'FAQs'].map((link, i) => <li key={i} data-id="element-2988">
                  <a href="#" className="text-[13px] text-[#aaa] hover:text-white" data-id="element-2989">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>
          <div data-id="element-2990">
            <div className="text-[14px] font-extrabold text-[#E8720C] mb-4" data-id="element-2991">
              Connect
            </div>
            <ul className="list-none space-y-2" data-id="element-2992">
              {['Refer a Friend', 'Partners & Influencers', 'Press Inquiries', 'Make a Return', 'Careers', 'Contact Us'].map((link, i) => <li key={i} data-id="element-2993">
                  <a href="#" className="text-[13px] text-[#aaa] hover:text-white" data-id="element-2994">
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>
        </div>
        <div className="max-w-[1100px] mx-auto border-t border-[#444] pt-6 flex flex-col md:flex-row justify-between items-start gap-6" data-id="element-2995">
          <div className="text-[11px] text-[#555] leading-[1.6] max-w-[420px]" data-id="element-2996">
            *These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </div>
          <div className="flex gap-3" data-id="element-2997">
            {['𝕏', 'in', '▶', 'f'].map((icon, i) => <a key={i} href="#" className="w-8.5 h-8.5 rounded-full border border-[#555] flex items-center justify-center text-[14px] text-[#aaa] transition-colors hover:border-[#E8720C] hover:text-[#E8720C]" data-id="element-2998">
                {icon}
              </a>)}
          </div>
        </div>
      </footer>
    </div>;
}